import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Volume2, VolumeX } from "lucide-react";
import { WA_SCHEDULE } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING =
  "Olá! Eu sou o RAFA, assistente da Clínica COB. Posso tirar dúvidas sobre nossas modalidades, localização e como agendar. Como posso ajudar?";
const CHIPS = [
  "Quero agendar uma consulta",
  "Quais são as modalidades?",
  "Onde fica a clínica?",
  "Quais convênios vocês aceitam?",
  "Qual o horário de funcionamento?",
];

const PRESET_VOICES = {
  "quero agendar uma consulta": "/api/files/cob-teresina/audio/rafa-agendar.mp3",
  "quais são as modalidades": "/api/files/cob-teresina/audio/rafa-modalidades.mp3",
  "onde fica a clínica": "/api/files/cob-teresina/audio/rafa-onde.mp3",
  "quais convênios vocês aceitam": "/api/files/cob-teresina/audio/rafa-convenios.mp3",
  "qual o horário de funcionamento": "/api/files/cob-teresina/audio/rafa-horarios.mp3",
};

const presetFor = (text) =>
  PRESET_VOICES[text.trim().toLowerCase().replace(/[?.!]+$/, "")] || null;

const getSessionId = () => {
  let id = localStorage.getItem("rafa_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("rafa_session", id);
  }
  return id;
};

export const RafaChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("rafa_voice") === "1");
  const [speaking, setSpeaking] = useState(false);
  const sessionRef = useRef(null);
  const listRef = useRef(null);
  const audioRef = useRef(null);
  const voiceRef = useRef(voiceOn);
  voiceRef.current = voiceOn;

  useEffect(() => {
    sessionRef.current = getSessionId();
    const openChat = () => setOpen(true);
    window.addEventListener("rafa:open", openChat);
    return () => window.removeEventListener("rafa:open", openChat);
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const stopVoice = () => {
    if (audioRef.current) audioRef.current.pause();
    setSpeaking(false);
  };

  const unlockVoice = () => {
    if (audioRef.current) return;
    const a = new Audio("/api/files/cob-teresina/audio/rafa-onde.mp3");
    a.volume = 0.01;
    a.play()
      .then(() => {
        a.pause();
        a.currentTime = 0;
        a.volume = 1;
      })
      .catch(() => {});
    audioRef.current = a;
  };

  const playAudio = (url) => {
    stopVoice();
    setSpeaking(true);
    const audio = audioRef.current || new Audio();
    audioRef.current = audio;
    audio.src = url;
    audio.onended = () => setSpeaking(false);
    audio.onerror = () => setSpeaking(false);
    audio.play().catch(() => setSpeaking(false));
  };

  const speak = async (text) => {
    try {
      setSpeaking(true);
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("tts failed");
      const { url } = await res.json();
      playAudio(`${process.env.REACT_APP_BACKEND_URL}${url}`);
    } catch {
      setSpeaking(false);
    }
  };

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem("rafa_voice", next ? "1" : "0");
    if (next) unlockVoice();
    else stopVoice();
  };

  const closeChat = () => {
    stopVoice();
    setOpen(false);
  };

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    if (voiceRef.current) unlockVoice();
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    let acc = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const snapshot = acc;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: snapshot };
          return copy;
        });
      }
    } catch {
      acc = "Desculpe, tive uma dificuldade agora. Fale com nossa equipe pelo WhatsApp +55 86 98190-9090.";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: acc };
        return copy;
      });
    } finally {
      setSending(false);
    }
    if (voiceRef.current && acc.trim()) {
      const preset = presetFor(text);
      if (preset) {
        playAudio(preset);
      } else {
        speak(acc);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="rafa-chat-button"
        aria-label="Abrir chat com o RAFA, assistente da Clínica COB"
        onClick={() => setOpen((v) => !v)}
        className="group fixed bottom-5 right-5 z-[60] block"
      >
        <span className="pointer-events-none absolute bottom-3 right-full mr-3 whitespace-nowrap rounded-full bg-cob-dark px-4 py-2 text-[12px] font-semibold text-white opacity-0 shadow-lg transition-all duration-300 translate-x-2 group-hover:translate-x-0 group-hover:opacity-100">
          Falar com o RAFA
        </span>
        <span className="relative block h-16 w-16 rounded-full animate-soft-pulse transition-transform duration-300 group-hover:scale-105">
          <img loading="lazy" decoding="async"
            src="/rafa-button.png"
            alt="RAFA, assistente virtual da Clínica COB"
            className="h-full w-full rounded-full object-cover shadow-[0_14px_35px_-10px_rgba(6,63,50,0.5)] ring-2 ring-cob-gold/60"
          />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="rafa-chat-panel"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-5 z-[65] flex h-[540px] max-h-[72vh] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-cob-light bg-white shadow-[0_30px_80px_-25px_rgba(6,63,50,0.5)]"
          >
            <div className="flex items-center gap-3 bg-cob-dark px-5 py-4">
              <span className="relative flex h-11 w-11 shrink-0 overflow-hidden rounded-full border-2 border-cob-gold/70">
                <img loading="lazy" decoding="async" src="/rafa-button.png" alt="RAFA" className="h-full w-full object-cover" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg font-semibold leading-tight text-white">RAFA</p>
                <p className="flex items-center gap-1.5 text-[11px] text-white/70">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Assistente da Clínica COB • online
                </p>
              </div>
              <button
                type="button"
                data-testid="rafa-voice-toggle"
                aria-label={voiceOn ? "Desativar voz do RAFA" : "Ativar voz do RAFA"}
                title={voiceOn ? "Voz ativada — tocar para desativar" : "Ativar voz do RAFA"}
                onClick={toggleVoice}
                className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                  voiceOn ? "bg-cob-gold text-cob-dark" : "text-white/60 hover:bg-white/10 hover:text-white"
                }`}
              >
                {voiceOn ? <Volume2 className="h-4.5 w-4.5" /> : <VolumeX className="h-4.5 w-4.5" />}
              </button>
              <button
                type="button"
                data-testid="rafa-chat-close"
                aria-label="Fechar chat"
                onClick={closeChat}
                className="flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={listRef} data-lenis-prevent className="flex-1 space-y-3 overflow-y-auto bg-cob-beige/40 px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`rafa-message-${m.role}`}
                    className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                      m.role === "user"
                        ? "rounded-br-md bg-cob-green text-white"
                        : "rounded-bl-md bg-white text-cob-ink"
                    }`}
                  >
                    {m.text || (
                      <span className="inline-flex gap-1 py-1">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cob-medium" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cob-medium [animation-delay:0.15s]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cob-medium [animation-delay:0.3s]" />
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {speaking && (
                <p data-testid="rafa-speaking" className="flex items-center gap-1.5 pl-1 text-[11px] font-medium text-cob-muted">
                  <Volume2 className="h-3.5 w-3.5 text-cob-gold" /> RAFA está falando...
                </p>
              )}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {CHIPS.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      data-testid={`rafa-chip-${chip.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => send(chip)}
                      className="rounded-full border border-cob-green/30 bg-white px-3.5 py-2 text-[12px] font-medium text-cob-green transition-colors hover:bg-cob-green hover:text-white"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-cob-light bg-white px-3.5 py-3">
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="rafa-chat-input"
                  placeholder="Escreva sua mensagem..."
                  className="min-w-0 flex-1 rounded-full border border-cob-light bg-cob-beige/40 px-4 py-2.5 text-sm text-cob-ink outline-none transition-colors focus:border-cob-green"
                />
                <button
                  type="button"
                  data-testid="rafa-chat-send"
                  aria-label="Enviar mensagem"
                  onClick={() => send()}
                  disabled={sending || !input.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cob-green text-white transition-all hover:bg-cob-dark disabled:opacity-50"
                >
                  <Send className="h-4.5 w-4.5" />
                </button>
              </div>
              <a
                href={WA_SCHEDULE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rafa-whatsapp-link"
                className="mt-2.5 block text-center text-[11px] font-medium text-cob-muted transition-colors hover:text-cob-green"
              >
                Prefere agendar agora? Chame no WhatsApp +55 86 98190-9090
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
